/**
 * 环境变量管理工具
 * 统一处理环境变量的加载和验证，确保环境变量的使用方式一致
 */

/**
 * 环境类型
 */
export enum Environment {
  DEVELOPMENT = 'development',
  TEST = 'test',
  PRODUCTION = 'production',
}

/**
 * 环境配置接口
 */
export interface EnvConfig {
  nodeEnv: Environment;
  port: number;
  apiBaseUrl: string;
  useMock: boolean;
  disableThirdParty: boolean;
  sessionSecret: string;
  isDev: boolean;
  isProd: boolean;
  isTest: boolean;
}

/**
 * 环境变量管理类
 */
export class EnvManager {
  private static instance: EnvManager;
  private config: EnvConfig;

  private constructor() {
    this.config = this.loadConfig();
  }

  /**
   * 获取单例实例
   */
  public static getInstance(): EnvManager {
    if (!EnvManager.instance) {
      EnvManager.instance = new EnvManager();
    }
    return EnvManager.instance;
  }

  /**
   * 加载环境配置
   */
  private loadConfig(): EnvConfig {
    const nodeEnv = this.getEnv('NODE_ENV', 'development');
    const port = this.getEnvAsNumber('PORT', 3000);
    const apiBaseUrl = this.getEnv('VITE_ACTIVE_HOST', 'http://localhost:3000');
    const useMock = this.getEnvAsBoolean('VITE_USE_MOCK', false);
    const disableThirdParty = this.getEnvAsBoolean('VITE_DISABLE_THIRD_PARTY', true);
    const sessionSecret = this.getEnv('SESSION_SECRET', 'great_secret_please_change');

    return {
      nodeEnv: nodeEnv as Environment,
      port,
      apiBaseUrl,
      useMock,
      disableThirdParty,
      sessionSecret,
      isDev: nodeEnv === Environment.DEVELOPMENT,
      isProd: nodeEnv === Environment.PRODUCTION,
      isTest: nodeEnv === Environment.TEST,
    };
  }

  /**
   * 获取环境变量
   * @param key 环境变量键
   * @param defaultValue 默认值
   * @returns 环境变量值
   */
  private getEnv(key: string, defaultValue: string): string {
    // 优先从 import.meta.env 获取（Vite 环境）
    try {
      // 使用动态访问方式检查 import.meta.env
      const importMeta = (globalThis as any).importMeta;
      if (importMeta && importMeta.env) {
        const value = importMeta.env[key] || importMeta.env[`VITE_${key}`];
        if (value !== undefined) {
          return value;
        }
      }
    } catch {
      // 忽略错误，继续尝试其他方式
    }

    // 从 process.env 获取（Node.js 环境）
    if (typeof process !== 'undefined' && process.env) {
      const value = process.env[key];
      if (value !== undefined) {
        return value;
      }
    }

    return defaultValue;
  }

  /**
   * 获取环境变量并转换为数字
   * @param key 环境变量键
   * @param defaultValue 默认值
   * @returns 环境变量值（数字）
   */
  private getEnvAsNumber(key: string, defaultValue: number): number {
    const value = this.getEnv(key, String(defaultValue));
    const num = Number(value);
    return isNaN(num) ? defaultValue : num;
  }

  /**
   * 获取环境变量并转换为布尔值
   * @param key 环境变量键
   * @param defaultValue 默认值
   * @returns 环境变量值（布尔值）
   */
  private getEnvAsBoolean(key: string, defaultValue: boolean): boolean {
    const value = this.getEnv(key, String(defaultValue));
    if (value === 'true') return true;
    if (value === 'false') return false;
    return defaultValue;
  }

  /**
   * 获取环境配置
   */
  public getConfig(): EnvConfig {
    return { ...this.config };
  }

  /**
   * 获取节点环境
   */
  public getNodeEnv(): Environment {
    return this.config.nodeEnv;
  }

  /**
   * 获取端口
   */
  public getPort(): number {
    return this.config.port;
  }

  /**
   * 获取 API 基础地址
   */
  public getApiBaseUrl(): string {
    return this.config.apiBaseUrl;
  }

  /**
   * 是否使用 mock 数据
   */
  public getUseMock(): boolean {
    return this.config.useMock;
  }

  /**
   * 是否禁用第三方脚本
   */
  public getDisableThirdParty(): boolean {
    return this.config.disableThirdParty;
  }

  /**
   * 获取会话密钥
   */
  public getSessionSecret(): string {
    return this.config.sessionSecret;
  }

  /**
   * 是否为开发环境
   */
  public isDevelopment(): boolean {
    return this.config.isDev;
  }

  /**
   * 是否为生产环境
   */
  public isProduction(): boolean {
    return this.config.isProd;
  }

  /**
   * 是否为测试环境
   */
  public isTest(): boolean {
    return this.config.isTest;
  }

  /**
   * 验证环境配置
   */
  public validateConfig(): void {
    if (!this.config.apiBaseUrl) {
      console.warn('警告: API 基础地址未配置，将使用默认值');
    }

    if (this.config.isProd && this.config.sessionSecret === 'great_secret_please_change') {
      console.warn('警告: 生产环境使用默认会话密钥，建议修改为安全的密钥');
    }

    if (this.config.isProd && this.config.useMock) {
      console.warn('警告: 生产环境启用了 mock 数据，建议禁用');
    }
  }
}

// 创建默认实例
export const envManager = EnvManager.getInstance();

// 验证环境配置
envManager.validateConfig();

// 导出常用的环境配置
export const getConfig = () => envManager.getConfig();
export const getNodeEnv = () => envManager.getNodeEnv();
export const getPort = () => envManager.getPort();
export const getApiBaseUrl = () => envManager.getApiBaseUrl();
export const getUseMock = () => envManager.getUseMock();
export const getDisableThirdParty = () => envManager.getDisableThirdParty();
export const getSessionSecret = () => envManager.getSessionSecret();
export const isDevelopment = () => envManager.isDevelopment();
export const isProduction = () => envManager.isProduction();
export const isTest = () => envManager.isTest();

// 导出环境配置常量
export const NODE_ENV = getNodeEnv();
export const PORT = getPort();
export const API_BASE_URL = getApiBaseUrl();
export const USE_MOCK = getUseMock();
export const DISABLE_THIRD_PARTY = getDisableThirdParty();
export const SESSION_SECRET = getSessionSecret();
export const IS_DEV = isDevelopment();
export const IS_PROD = isProduction();
export const IS_TEST = isTest();
